import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import RobotAvatar from "./RobotAvatar";
import ChatPanel from "./ChatPanel";
import { consumePendingChat, openChat } from "@/lib/chatBus";
import { api } from "@/lib/api";

/**
 * Omnipresent Master Chat — floating robot bubble bottom-right.
 * The panel stays mounted (hidden via CSS) so the conversation, session and
 * streaming survive closing/reopening. Opens compact; can expand fullscreen.
 * Other pages open it via openChat() from @/lib/chatBus.
 */
export default function ChatWidget() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [unread, setUnread] = useState(false);
    const [incoming, setIncoming] = useState(null); // {masterId?, context?, prompt?, ts}
    const [tip, setTip] = useState(null);           // proactieve dagtip {kind, n, master_id}
    const openRef = useRef(open);
    openRef.current = open;

    useEffect(() => {
        const onOpen = (e) => {
            consumePendingChat(); // handled live — drop the buffered copy
            setIncoming({ ...(e.detail || {}), ts: Date.now() });
            setOpen(true);
            setUnread(false);
        };
        window.addEventListener("jr:open-chat", onOpen);
        // A legacy /app/chat deep-link may have fired openChat() before this
        // widget mounted (full page load) — pick up the buffered intent.
        const buffered = consumePendingChat();
        if (buffered) {
            setIncoming({ ...buffered, ts: Date.now() });
            setOpen(true);
        }
        return () => window.removeEventListener("jr:open-chat", onOpen);
    }, []);

    // Let other floating UI (feedback FAB) hide itself while the panel is open.
    useEffect(() => {
        document.body.toggleAttribute("data-jr-chat-open", open);
        return () => document.body.removeAttribute("data-jr-chat-open");
    }, [open]);

    // Proactieve dagtip: één spraakbubbel per dag, kort na het inladen.
    useEffect(() => {
        const today = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem("jr-daytip-shown") === today) return;
        let alive = true;
        const show = setTimeout(async () => {
            try {
                const { data } = await api.get("/agents/chat/daytip");
                // Chat al open of unmounted → geen tip tonen en key NIET zetten
                // (volgende page-load krijgt de gebruiker de tip alsnog).
                if (!alive || openRef.current) return;
                setTip(data);
                localStorage.setItem("jr-daytip-shown", today);
            } catch { /* non-blocking */ }
        }, 1800);
        return () => { alive = false; clearTimeout(show); };
    }, []);

    // Auto-hide de dagtip na 30s of zodra de chat opent.
    useEffect(() => {
        if (!tip) return;
        if (open) { setTip(null); return; }
        const hide = setTimeout(() => setTip(null), 30000);
        return () => clearTimeout(hide);
    }, [tip, open]);

    const openTip = () => {
        const t2 = tip;
        setTip(null);
        openChat({
            masterId: t2.master_id,
            prompt: t(`chat.tipprompt_${t2.kind}`, { n: t2.n }),
        });
    };

    const toggle = () => setOpen((o) => { if (!o) setUnread(false); return !o; });
    const onAgentDone = () => { if (!openRef.current) setUnread(true); };

    return (
        <>
            {/* Panel — kept mounted so the conversation survives close/open */}
            <div
                data-testid="chat-widget-panel"
                className={`${open ? "jr-chat-panel" : "hidden"} fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-white/12 bg-[var(--jr-bg-2)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)] ${
                    expanded
                        ? "inset-3 sm:inset-6"
                        : "bottom-[92px] right-4 sm:right-6 w-[min(420px,calc(100vw-32px))] h-[min(640px,calc(100vh-120px))]"
                }`}
            >
                <ChatPanel
                    expanded={expanded}
                    onToggleExpand={() => setExpanded((v) => !v)}
                    onClose={() => setOpen(false)}
                    incoming={incoming}
                    onAgentDone={onAgentDone}
                />
            </div>

            {/* Proactieve dagtip — spraakbubbel van de robot */}
            {!open && tip && (
                <div data-testid="chat-daytip" className="jr-chat-panel fixed bottom-[92px] right-4 sm:right-6 z-50 w-[280px]">
                    <div className="relative rounded-2xl rounded-br-sm border border-[var(--jr-brand)]/35 bg-[var(--jr-bg-2)] shadow-[0_20px_50px_-16px_rgba(0,0,0,0.6)] p-3.5">
                        <button type="button" onClick={() => setTip(null)} data-testid="chat-daytip-dismiss"
                            aria-label={t("chat.bubble_close")}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-[var(--jr-text-3)] hover:text-white">
                            <X size={11}/>
                        </button>
                        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[var(--jr-brand)] mb-1">{t("chat.tip_title")}</div>
                        <button type="button" onClick={openTip} data-testid="chat-daytip-text"
                            className="text-left text-sm text-white leading-relaxed hover:text-[var(--jr-brand)] transition-colors pr-3">
                            {t(`chat.tip_${tip.kind}`, { n: tip.n })}
                        </button>
                        {/* staartje richting de robot */}
                        <span aria-hidden className="absolute -bottom-[7px] right-6 w-3.5 h-3.5 rotate-45 bg-[var(--jr-bg-2)] border-b border-r border-[var(--jr-brand)]/35"/>
                    </div>
                </div>
            )}

            {/* Floating robot bubble */}
            <button
                type="button"
                onClick={toggle}
                aria-label={open ? t("chat.bubble_close") : t("chat.bubble_open")}
                title={open ? t("chat.bubble_close") : t("chat.bubble_open")}
                data-testid="chat-fab"
                className={`${open ? "" : "jr-chat-fab"} fixed bottom-4 right-4 sm:bottom-5 sm:right-6 z-50 w-14 h-14 rounded-full grid place-items-center bg-[var(--jr-surface-2)] border-2 border-[var(--jr-brand)] shadow-[0_12px_36px_-8px_rgba(0,0,0,0.6)] hover:scale-105 transition-transform`}
            >
                {open ? <X size={20} className="text-[var(--jr-text-2)]"/> : <RobotAvatar size={40}/>}
                {!open && unread && (
                    <span data-testid="chat-unread-dot" className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[var(--jr-amber)] border-2 border-[var(--jr-bg)]">
                        <span className="absolute inset-0 rounded-full bg-[var(--jr-amber)] animate-ping opacity-60"/>
                    </span>
                )}
            </button>
        </>
    );
}
